import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Eye, History, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Container } from '../layouts/Container';
import { selectUserToken } from '../redux/features/user/userSelectors';
import { AutoImportRuleModal } from '../features/parser-import/components/AutoImportRuleModal';
import { RuleHistoryModal } from '../features/parser-import/components/RuleHistoryModal';
import { RulePreviewRecordsModal } from '../features/parser-import/components/RulePreviewRecordsModal';
import {
  useDeleteImportRuleMutation,
  useListImportRulesQuery,
  useRunImportRuleMutation,
  useToggleImportRuleMutation,
} from '../features/parser-import/api/parserImportApi';

const fmtDate = value => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const cooldownLabel = seconds => {
  const value = Number(seconds || 0);
  return value < 3600
    ? `${Math.round(value / 60)} min`
    : `${Math.round(value / 3600)} h`;
};

const filtersSummary = filters => {
  const parts = [];
  if (filters?.radius_km) parts.push(`${filters.radius_km} km`);
  if (filters?.cities?.length) parts.push(`${filters.cities.length} cities`);
  if (filters?.sources?.length) parts.push(`${filters.sources.length} sources`);
  if (filters?.types?.length) parts.push(`${filters.types.length} types`);
  if (filters?.price_min || filters?.price_max) {
    parts.push(`€${filters.price_min || 0}–${filters.price_max || '∞'}`);
  }
  if (filters?.name_pattern) parts.push(filters.name_pattern);
  if (filters?.hide_imported) parts.push('hide imported');
  return parts.length ? parts.join(' · ') : 'All parser records';
};

export const AutoImportRules = () => {
  const token = useSelector(selectUserToken);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [editingRule, setEditingRule] = useState(null);
  const [previewingRule, setPreviewingRule] = useState(null);
  const [historyRule, setHistoryRule] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const { data, isLoading, error } = useListImportRulesQuery(
    { token, page, per_page: 15 },
    { skip: !token }
  );
  const [toggleRule] = useToggleImportRuleMutation();
  const [runRule, runState] = useRunImportRuleMutation();
  const [deleteRule] = useDeleteImportRuleMutation();
  const rows = data?.data ?? [];
  const meta = data?.meta;

  const handleToggle = async rule => {
    try {
      await toggleRule({ token, ruleId: rule.id, enabled: !rule.enabled }).unwrap();
    } catch (toggleError) {
      toast.error(toggleError?.data?.message || 'Could not update rule');
    }
  };

  const handleRun = async rule => {
    try {
      const result = await runRule({ token, ruleId: rule.id }).unwrap();
      if (result?.batch_id) toast.success(`Auto-import batch #${result.batch_id} created`);
      else toast.info('No batch created for this rule');
    } catch (runError) {
      toast.error(runError?.data?.message || 'Could not run rule');
    }
  };

  const handleDelete = async rule => {
    if (!window.confirm(`Delete auto-import rule “${rule.name}”?`)) return;
    try {
      await deleteRule({ token, ruleId: rule.id }).unwrap();
      toast.success('Auto-import rule deleted');
    } catch (deleteError) {
      toast.error(deleteError?.data?.message || 'Could not delete rule');
    }
  };

  return (
    <Container>
      <div
        className="flex flex-col items-start gap-4 w-full bg-white"
        style={{ minHeight: 'calc(100vh - 5rem)' }}
      >
        <div className="flex items-center justify-between w-full gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <RefreshCw size={18} className="text-purpleColor" />
            <h1 className="text-lg font-semibold text-blackColor">Auto-import rules</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/settings/parser/imports')}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-gray-200 text-darkGrayColor hover:bg-lightGrayColor"
            >
              <History size={13} /> History
            </button>
            <button
              type="button"
              onClick={() => setIsCreateOpen(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-purpleColor bg-purpleColor text-white"
            >
              <Plus size={13} /> New rule
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="flex flex-col gap-1 w-full">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-10 rounded bg-lightGrayColor animate-pulse" />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div className="text-sm text-redColor">Failed to load auto-import rules.</div>
        )}

        {!isLoading && !error && rows.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 w-full py-16 border border-dashed border-gray-200 rounded-lg bg-lightestGrayColor">
            <span className="text-sm font-medium text-blackColor">No auto-import rules yet</span>
            <span className="text-xs text-darkGrayColor">
              Create your first rule to keep parser matches flowing into import batches.
            </span>
            <button
              type="button"
              onClick={() => setIsCreateOpen(true)}
              className="px-3 py-1.5 text-xs rounded border border-purpleColor bg-purpleColor text-white"
            >
              Create your first rule
            </button>
          </div>
        )}

        {!isLoading && !error && rows.length > 0 && (
          <div className="w-full overflow-auto">
            <table className="min-w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 bg-gray-50">
                <tr className="border-b border-gray-200">
                  {['Enabled', 'Name', 'Filters', 'Last run', 'Matched', 'Cooldown', 'Actions'].map(label => (
                    <th
                      key={label}
                      className="px-3 py-2 text-left text-xs font-semibold text-gray-600 whitespace-nowrap"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(rule => (
                  <tr key={rule.id} className="border-b border-gray-100 hover:bg-lightestGrayColor">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={!!rule.enabled}
                        onChange={() => handleToggle(rule)}
                        className="accent-purpleColor"
                      />
                    </td>
                    <td className="px-3 py-2 text-xs text-blackColor font-medium whitespace-nowrap">
                      {rule.name}
                    </td>
                    <td className="px-3 py-2 text-xs text-darkGrayColor min-w-[260px]">
                      {filtersSummary(rule.filters)}
                    </td>
                    <td className="px-3 py-2 text-xs text-darkGrayColor whitespace-nowrap">
                      <div className="flex flex-col gap-0.5">
                        <span>{fmtDate(rule.last_run_at)}</span>
                        {rule.last_run_status && (
                          <span className="text-[10px] uppercase tracking-wide text-purpleColor">
                            {rule.last_run_status}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-blackColor font-medium">
                      {Number(rule.records_matched_last_run || 0).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-3 py-2 text-xs text-darkGrayColor whitespace-nowrap">
                      {cooldownLabel(rule.cooldown_seconds)}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingRule(rule)}
                          className="text-purpleColor hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setPreviewingRule(rule)}
                          className="inline-flex items-center gap-1 text-darkGrayColor hover:underline"
                        >
                          <Eye size={12} /> Preview
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRun(rule)}
                          disabled={runState.isLoading}
                          className="text-greenColor hover:underline disabled:opacity-50"
                        >
                          Run now
                        </button>
                        <button
                          type="button"
                          onClick={() => setHistoryRule(rule)}
                          className="text-darkGrayColor hover:underline"
                        >
                          History
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(rule)}
                          className="inline-flex items-center gap-1 text-redColor hover:underline"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta?.last_page > 1 && (
          <div className="flex items-center gap-2 w-full justify-end text-xs">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(current => Math.max(1, current - 1))}
              className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40"
            >
              Prev
            </button>
            <span className="text-darkGrayColor">Page {page} / {meta.last_page}</span>
            <button
              type="button"
              disabled={page >= meta.last_page}
              onClick={() => setPage(current => Math.min(meta.last_page, current + 1))}
              className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {isCreateOpen && (
        <AutoImportRuleModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          token={token}
        />
      )}
      {editingRule && (
        <AutoImportRuleModal
          isOpen={!!editingRule}
          onClose={() => setEditingRule(null)}
          token={token}
          initialRule={editingRule}
        />
      )}
      {previewingRule && (
        <RulePreviewRecordsModal
          rule={previewingRule}
          token={token}
          onClose={() => setPreviewingRule(null)}
        />
      )}
      {historyRule && (
        <RuleHistoryModal
          rule={historyRule}
          token={token}
          onClose={() => setHistoryRule(null)}
        />
      )}
    </Container>
  );
};
